/**
 * Enhanced AuthProvider - User Authentication Only
 * CLEANED VERSION - No admin interference, proper scope separation
 *
 * Handles ONLY user authentication:
 * - User login/logout and session management
 * - Enhanced JWT token management for users
 * - Session warnings and expiry handling
 * - Cross-tab synchronization for user sessions
 * - Security event logging for user actions
 *
 * SCOPE: User authentication only (auth_token)
 * ADMIN: Handled by existing admin system (completely separate)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import enhancedTokenManager from '../utils/enhancedTokenManager';
import apiClient from '../api/client';
import productionLogger from '../utils/monitoringFix';
import securityMonitor from '../utils/securityMonitor';
import authPerformanceMonitor from '../utils/authPerformance';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Core user authentication state only
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Session management state
  const [sessionWarning, setSessionWarning] = useState(null);
  const [sessionStatus, setSessionStatus] = useState('active'); // active, warning, expiring, expired

  const [stytchSessionToken, setStytchSessionToken] = useState(null);
  const [stytchSessionJwt, setStytchSessionJwt] = useState(null);

  // Refs for cleanup and preventing race conditions
  const tokenListenerRef = useRef(null);
  const mountedRef = useRef(true);
  const initializationRef = useRef(false);

  /**
   * Safe state setter that checks if component is still mounted
   */
  const safeSetState = useCallback((setter, value) => {
    if (mountedRef.current) {
      setter(value);
    }
  }, []);

  /**
   * Initialize user authentication state and listeners
   */
  useEffect(() => {
    // Prevent multiple initializations
    if (initializationRef.current) {
      return;
    }
    initializationRef.current = true;

    // Add safety timeout to prevent hanging loading state
    const initTimeout = setTimeout(() => {
      console.warn('⚠️ AuthProvider initialization timeout - forcing completion');
      if (mountedRef.current) {
        safeSetState(setIsLoading, false);
      }
    }, 10000); // 10 second timeout

    initializeUserAuth().finally(() => {
      clearTimeout(initTimeout);
    });

    // Set up cleanup
    return () => {
      mountedRef.current = false;
      clearTimeout(initTimeout);
      if (tokenListenerRef.current) {
        tokenListenerRef.current(); // Cleanup listener
      }
    };
  }, []);

  /**
   * Initialize user authentication state from stored tokens
   * SCOPE: User authentication only
   */
  const initializeUserAuth = async () => {
    try {
      // Ensure component is marked as mounted during initialization
      mountedRef.current = true;
      setIsLoading(true);
      setError(null);

      // Check for existing user auth token only
      try {
        const authToken = await enhancedTokenManager.getToken('auth_token');
        if (authToken) {
          await loadUserFromToken(authToken);
        }
      } catch (error) {
        console.warn('Failed to load auth token:', error);
        // Don't fail entire initialization for auth token issues
      }

      // Set up token manager listeners for user tokens
      if (mountedRef.current) {
        setupTokenManagerListeners();
      }

    } catch (error) {
      console.error('Failed to initialize user auth:', error);
      setError('Failed to initialize user authentication');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Set up token manager event listeners for user tokens only
   */
  const setupTokenManagerListeners = () => {
    if (!mountedRef.current) return;

    tokenListenerRef.current = enhancedTokenManager.addEventListener((event) => {
      if (!mountedRef.current) return;

      switch (event.type) {
        case 'session_warning':
          handleSessionWarning(event);
          break;
        case 'token_change':
          handleTokenChange(event);
          break;
        case 'token_expired':
          handleTokenExpired(event);
          break;
        default:
          break;
      }
    });
  };

  /**
   * Handle session warning events
   */
  const handleSessionWarning = (event) => {
    if (!mountedRef.current) return;

    safeSetState(setSessionWarning, {
      threshold: event.threshold,
      timeRemaining: event.timeRemaining,
      minutes: event.minutes,
      message: event.message
    });

    // Update session status based on threshold
    if (event.threshold === '2min') {
      safeSetState(setSessionStatus, 'expiring');
    } else if (event.threshold === '5min') {
      safeSetState(setSessionStatus, 'warning');
    } else {
      safeSetState(setSessionStatus, 'warning');
    }

    // Log session warning
    try {
      productionLogger.logSecurityEvent('session_warning', {
        threshold: event.threshold,
        userId: user?.id || 'unknown',
        timestamp: Date.now()
      });
    } catch (error) {
      console.warn('Failed to log session warning:', error);
    }
  };

  /**
   * Handle token change events from other tabs
   */
  const handleTokenChange = async (event) => {
    if (!mountedRef.current || event.source !== 'other_tab') {
      return;
    }

    try {
      // Refresh user state when auth tokens change in other tabs
      const authToken = await enhancedTokenManager.getToken('auth_token');
      if (authToken) {
        await loadUserFromToken(authToken);
      } else {
        // No token - clear user state
        safeSetState(setUser, null);
        safeSetState(setIsAuthenticated, false);
      }
    } catch (error) {
      console.warn('Failed to handle token change:', error);
    }
  };

  /**
   * Handle token expiration
   */
  const handleTokenExpired = (event) => {
    if (!mountedRef.current) return;

    console.log('🔓 User token expired - clearing user state and redirecting to login');

    // Clear user state
    safeSetState(setUser, null);
    safeSetState(setIsAuthenticated, false);
    safeSetState(setSessionWarning, null);
    safeSetState(setSessionStatus, 'expired');

    // Clear user tokens
    localStorage.removeItem('auth_token');
    localStorage.removeItem('authToken');

    // Redirect to login if not already there
    if (window.location.pathname !== '/login') {
      window.location.href = '/login?reason=session_expired';
    }
  };

  /**
   * Load user data from auth token
   */
  const loadUserFromToken = async (token) => {
    try {
      const payload = enhancedTokenManager.decodeTokenPayload(token);

      // Basic user info from token
      const userData = {
        id: payload.sub,
        email: payload.email,
        role: payload.role,
        name: payload.name,
        clinicId: payload.clinic_id,
        permissions: payload.permissions || []
      };

      setUser(userData);
      setIsAuthenticated(true);

    } catch (error) {
      console.error('Failed to load user from token:', error);
      // Clear user state on token parsing failure
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  /**
   * User login
   */
  const login = async (email, password, rememberMe = false) => {
    const perfOp = authPerformanceMonitor.startOperation('login');

    try {
      safeSetState(setIsLoading, true);
      safeSetState(setError, null);

      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);
      if (rememberMe) {
        formData.append('remember_me', 'true');
      }

      const response = await apiClient.post('/auth/stytch/password/authenticate', {
        email,
        password
      });

      const data = response?.data ?? {};

      if (data.member_authenticated === false) {
        const mfaPayload = {
          intermediateSessionToken: data.intermediate_session_token,
          memberId: data.member_id,
          organizationId: data.organization_id,
          organization: data.organization || {},
          mfaRequired: data.mfa_required || {},
          email,
          rememberMe
        };

        authPerformanceMonitor.endOperation(perfOp, true, { mfaRequired: true });
        return { success: false, mfaRequired: true, data: mfaPayload };
      }

      if (data.success && data.access_token) {
        await enhancedTokenManager.setToken(data.access_token, 'auth_token');
        await loadUserFromToken(data.access_token);

        setStytchSessionToken(data.session_token || null);
        setStytchSessionJwt(data.session_jwt || null);

        safeSetState(setSessionWarning, null);
        safeSetState(setSessionStatus, 'active');

        const sessionId = securityMonitor.trackLogin(
          user?.id || email,
          {
            email,
            rememberMe,
            userAgent: navigator.userAgent
          }
        );

        try {
          productionLogger.logSecurityEvent('user_login', {
            userId: user?.id,
            email,
            sessionId,
            timestamp: Date.now(),
            viaMfa: false
          });
        } catch (logError) {
          console.warn('Failed to log login event:', logError);
        }

        authPerformanceMonitor.endOperation(perfOp, true);
        return { success: true, sessionId };
      }

      throw new Error(data.message || 'Login failed');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Login failed';
      const errorMessage = Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : detail;
      safeSetState(setError, errorMessage);

      const lockoutStatus = securityMonitor.trackFailedLogin(email, errorMessage);

      try {
        productionLogger.logSecurityEvent('login_failure', {
          email,
          error: errorMessage,
          lockoutStatus,
          timestamp: Date.now()
        });
      } catch (logError) {
        console.warn('Failed to log login failure:', logError);
      }

      authPerformanceMonitor.endOperation(perfOp, false, { error: errorMessage });

      if (lockoutStatus.shouldLockout) {
        const lockoutError = `Account locked due to too many failed attempts. Try again after ${new Date(lockoutStatus.nextAllowedAttempt).toLocaleTimeString()}`;
        safeSetState(setError, lockoutError);
        return { success: false, error: lockoutError, locked: true };
      }

      return {
        success: false,
        error: errorMessage,
        remainingAttempts: lockoutStatus.remainingAttempts
      };
    } finally {
      safeSetState(setIsLoading, false);
    }
  };

  /**
   * Complete MFA verification after primary auth
   */
  const completeMfa = async ({
    method,
    code,
    organizationId,
    memberId,
    intermediateSessionToken,
    sessionToken,
    sessionJwt,
    totpRegistrationId,
    email,
    rememberMe = false
  }) => {
    const perfOp = authPerformanceMonitor.startOperation('mfa_verify');

    try {
      safeSetState(setIsLoading, true);
      safeSetState(setError, null);

      const payload = {
        method,
        code,
        organization_id: organizationId,
        member_id: memberId
      };

      if (intermediateSessionToken) {
        payload.intermediate_session_token = intermediateSessionToken;
      }
      if (sessionToken) {
        payload.session_token = sessionToken;
      }
      if (sessionJwt) {
        payload.session_jwt = sessionJwt;
      }
      if (totpRegistrationId) {
        payload.totp_registration_id = totpRegistrationId;
      }

      const response = await apiClient.post('/auth/stytch/mfa/verify', payload);
      const data = response?.data ?? {};

      if (data.success && data.access_token) {
        await enhancedTokenManager.setToken(data.access_token, 'auth_token');
        await loadUserFromToken(data.access_token);

        if (data.session_token || data.session_jwt) {
          setStytchSessionToken(data.session_token || null);
          setStytchSessionJwt(data.session_jwt || null);
        }

        safeSetState(setSessionWarning, null);
        safeSetState(setSessionStatus, 'active');

        const sessionId = securityMonitor.trackLogin(
          user?.id || memberId || email,
          {
            email,
            rememberMe,
            userAgent: navigator.userAgent,
            method,
            viaMfa: true
          }
        );

        try {
          productionLogger.logSecurityEvent('user_login', {
            userId: user?.id,
            email,
            sessionId,
            timestamp: Date.now(),
            viaMfa: true,
            mfaMethod: method
          });
        } catch (logError) {
          console.warn('Failed to log MFA login event:', logError);
        }

        authPerformanceMonitor.endOperation(perfOp, true, { viaMfa: true, method });
        return { success: true, sessionId };
      }

      throw new Error(data.message || 'MFA verification failed');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'MFA verification failed';
      const errorMessage = Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : detail;
      safeSetState(setError, errorMessage);

      if (email) {
        try {
          productionLogger.logSecurityEvent('login_failure', {
            email,
            error: errorMessage,
            context: 'mfa_verify',
            timestamp: Date.now()
          });
        } catch (logError) {
          console.warn('Failed to log MFA failure event:', logError);
        }

        securityMonitor.trackFailedLogin(email, errorMessage);
      }

      authPerformanceMonitor.endOperation(perfOp, false, { error: errorMessage, method });
      return { success: false, error: errorMessage };
    } finally {
      safeSetState(setIsLoading, false);
    }
  };

  /**
   * Trigger MFA challenge (e.g., resend SMS, enroll TOTP)
   */
  const sendMfaChallenge = async ({
    method,
    organizationId,
    memberId,
    phoneNumber,
    intermediateSessionToken,
    sessionToken,
    sessionJwt,
    totpRegistrationId
  }) => {
    try {
      const payload = {
        method,
        organization_id: organizationId,
        member_id: memberId
      };

      if (phoneNumber) {
        payload.phone_number = phoneNumber;
      }
      if (intermediateSessionToken) {
        payload.intermediate_session_token = intermediateSessionToken;
      }
      if (sessionToken) {
        payload.session_token = sessionToken;
      }
      if (sessionJwt) {
        payload.session_jwt = sessionJwt;
      }
      if (totpRegistrationId) {
        payload.totp_registration_id = totpRegistrationId;
      }

      const response = await apiClient.post('/auth/stytch/mfa/challenge', payload);
      return { success: true, data: response.data };
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Failed to send MFA challenge';
      const errorMessage = Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : detail;
      return { success: false, error: errorMessage };
    }
  };

  /**
   * User logout
   * During impersonation, this should stop impersonation and return to admin view
   */
  const logout = async () => {
    try {
      safeSetState(setIsLoading, true);

      // Log before clearing user state
      const currentUserId = user?.id;

      // Check if we're impersonating - if so, just stop impersonation
      const token = enhancedTokenManager.getToken('auth_token');
      if (token) {
        try {
          const payload = enhancedTokenManager.decodeTokenPayload(token);
          if (payload.impersonation && payload.impersonation.is_impersonated) {
            // Stop impersonation instead of full logout
            await apiClient.post('/auth/stop-impersonation');
            // Token will be updated by the API response
            window.location.reload();
            return;
          }
        } catch (err) {
          console.warn('Error checking impersonation status during logout:', err);
        }
      }

      // Call logout API for regular logout
      try {
        await apiClient.post('/auth/logout');
      } catch (error) {
        // Continue with logout even if API call fails
        console.warn('Logout API call failed:', error);
      }

      // Clear tokens and state using enhanced token manager
      enhancedTokenManager.clearToken('auth_token');
      safeSetState(setUser, null);
      safeSetState(setIsAuthenticated, false);
      safeSetState(setSessionWarning, null);
      safeSetState(setSessionStatus, 'active');
      safeSetState(setError, null);
      setStytchSessionToken(null);
      setStytchSessionJwt(null);

      try {
        productionLogger.logSecurityEvent('user_logout', {
          userId: currentUserId,
          timestamp: Date.now()
        });
      } catch (logError) {
        console.warn('Failed to log logout event:', logError);
      }

    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      safeSetState(setIsLoading, false);
    }
  };

  /**
   * Admin logout - clears admin session only
   */
  const adminLogout = async () => {
    try {
      // Import AdminAuthAPI dynamically to avoid circular dependencies
      const { default: AdminAuthAPI } = await import('../api/admin/auth.js');

      // Call admin logout
      await AdminAuthAPI.logout();

      // Redirect to admin login
      window.location.href = '/admin/login';
    } catch (error) {
      console.error('Admin logout error:', error);
      // Force redirect even if logout fails
      window.location.href = '/admin/login';
    }
  };

  /**
   * Extend session (refresh tokens)
   */
  const extendSession = async () => {
    try {
      await enhancedTokenManager.refreshToken('auth_token', true);

      // Clear session warnings
      safeSetState(setSessionWarning, null);
      safeSetState(setSessionStatus, 'active');

      try {
        productionLogger.logSecurityEvent('session_extended', {
          userId: user?.id,
          timestamp: Date.now()
        });
      } catch (logError) {
        console.warn('Failed to log session extension:', logError);
      }

      return { success: true };
    } catch (error) {
      const errorMessage = 'Failed to extend session';
      safeSetState(setError, errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Dismiss session warning
   */
  const dismissSessionWarning = () => {
    safeSetState(setSessionWarning, null);
    if (sessionStatus === 'warning') {
      safeSetState(setSessionStatus, 'active');
    }
  };

  /**
   * Get current session status
   */
  const getSessionStatus = () => {
    return {
      isActive: sessionStatus === 'active',
      hasWarning: sessionStatus === 'warning',
      isExpiring: sessionStatus === 'expiring',
      warning: sessionWarning
    };
  };

  /**
   * Check if user has specific permission
   */
  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || false;
  };

  /**
   * Check if user has admin role (user level, not system admin)
   */
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  // Context value - CLEAN and FOCUSED on user authentication only
  const value = {
    // Core user auth state
    user,
    isAuthenticated,
    isLoading,
    error,

    // Session state
    sessionWarning,
    sessionStatus,

    // User auth actions
    login,
    completeMfa,
    sendMfaChallenge,
    logout,
    adminLogout,

    // Session actions
    extendSession,
    dismissSessionWarning,
    getSessionStatus,

    // Utility functions
    hasPermission,
    isAdmin,

    // Token manager access
    tokenManager: enhancedTokenManager,

    // Stytch session
    stytchSessionToken,
    stytchSessionJwt
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
